import { createColumnHelper, Row } from '@tanstack/react-table';
import { PlusIcon } from 'lucide-react';
import { useNavigate } from 'react-router';

import { Button } from '@/components/core/button';
import { Card, CardAction, CardHeader } from '@/components/core/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
} from '@/components/data-table';
import { StatusBadge } from '@/components/status-badge';
import type { SimulationScenarioDTO } from '@/gateways/impl/SimulationGateway';
import { formatDateTime } from '@/shared/helpers';
import { simulationScenarioRunStatusType } from '@/shared/status-maps';
import { simulationScenarioRunStatusTitle } from '@/shared/title-maps';

const columnHelper = createColumnHelper<SimulationScenarioDTO>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({ getValue }) => getValue() || '—',
  }),
  columnHelper.accessor('id', {
    header: 'Scenario ID',
    cell: ({ getValue }) => getValue().slice(0, 8),
  }),
  columnHelper.accessor('startedAt', {
    header: 'Started',
    cell: ({ getValue }) => {
      const startedAt = getValue();
      return startedAt ? formatDateTime(startedAt) : '—';
    },
  }),
  columnHelper.accessor('runStatus', {
    header: 'Status',
    cell: ({ getValue }) => (
      <StatusBadge
        status={getValue()}
        type={simulationScenarioRunStatusType}
        title={simulationScenarioRunStatusTitle}
      />
    ),
    meta: { align: 'right' },
  }),
];

type Props = {
  simulationId: string | undefined;
  scenarios: SimulationScenarioDTO[];
  isLoading?: boolean;
  canCreateScenario: boolean;
  disabledReason: string;
};

export function SimulationScenariosCard({
  simulationId,
  scenarios,
  isLoading,
  canCreateScenario,
  disabledReason,
}: Props) {
  const navigate = useNavigate();

  const table = useDataTable({
    data: scenarios,
    columns,
    isLoading,
    manualPagination: true,
    manualSorting: false,
  });

  const onClickRow = (row: Row<SimulationScenarioDTO>) => {
    if (!simulationId) return;
    void navigate(`/simulations/${simulationId}/scenarios/${row.original.id}`);
  };

  const buttonDisabled = isLoading || !simulationId || !canCreateScenario;

  const newScenarioButton = (
    <Button
      onClick={() => simulationId && navigate(`/simulations/${simulationId}/scenarios/new`)}
      disabled={buttonDisabled}
      aria-disabled={buttonDisabled}
    >
      <PlusIcon />
      New scenario
    </Button>
  );

  return (
    <Card>
      <CardHeader>
        <DataTableCardTitle table={table}>Scenarios</DataTableCardTitle>
        <CardAction>
          {buttonDisabled && disabledReason ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{newScenarioButton}</span>
              </TooltipTrigger>
              <TooltipContent>{disabledReason}</TooltipContent>
            </Tooltip>
          ) : (
            newScenarioButton
          )}
        </CardAction>
      </CardHeader>
      <DataTableCardBody table={table}>
        <DataTable table={table} onClickRow={onClickRow} />
      </DataTableCardBody>
    </Card>
  );
}
